/**
 * Barber create/edit form component
 */
import { useState, useEffect } from 'react';
import { Barber } from '@/types';
import { CreateBarberInput, UpdateBarberInput, useBarber } from '@/contexts/BarberContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ImageUpload } from '@/components/shared/ImageUpload';

export interface CreateBarberWithCredentials extends CreateBarberInput {
  email: string;
  password: string;
}

interface BarberFormProps {
  barber?: Barber;
  franchiseId: string;
  branchId: string;
  userId: string;
  onSubmit: (data: CreateBarberInput | UpdateBarberInput | CreateBarberWithCredentials) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function BarberForm({ barber, franchiseId, branchId, userId, onSubmit, onCancel, isLoading }: BarberFormProps) {
  const { uploadPhoto } = useBarber();
  const [uploading, setUploading] = useState(false);
  const isEditing = !!barber;

  // Campos de credenciales (solo para crear)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [formData, setFormData] = useState<CreateBarberInput>({
    userId,
    franchiseId,
    branchId,
    displayName: '',
    photoURL: undefined,
    specialties: [],
    bio: '',
    schedule: {
      monday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
      tuesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
      wednesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
      thursday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
      friday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
      saturday: { isWorking: true, workStart: '10:00', workEnd: '15:00' },
      sunday: { isWorking: false },
    },
    isActive: true,
    isAvailable: true,
  });

  const [specialtyInput, setSpecialtyInput] = useState('');

  // Default schedule to use when barber doesn't have one
  const defaultSchedule = {
    monday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    tuesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    wednesday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    thursday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    friday: { isWorking: true, workStart: '09:00', workEnd: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    saturday: { isWorking: true, workStart: '10:00', workEnd: '15:00' },
    sunday: { isWorking: false },
  };

  useEffect(() => {
    if (barber) {
      setFormData({
        userId: barber.userId,
        franchiseId: barber.franchiseId,
        branchId: barber.branchId,
        displayName: barber.displayName,
        photoURL: barber.photoURL,
        specialties: barber.specialties || [],
        bio: barber.bio || '',
        schedule: barber.schedule || defaultSchedule,
        isActive: barber.isActive,
        isAvailable: barber.isAvailable,
      });
    }
  }, [barber]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones para crear
    if (!isEditing) {
      if (!email) {
        alert('El email es obligatorio');
        return;
      }
      if (!password) {
        alert('La contraseña es obligatoria');
        return;
      }
      if (password.length < 6) {
        alert('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (password !== confirmPassword) {
        alert('Las contraseñas no coinciden');
        return;
      }

      // Enviar datos con credenciales
      const dataWithCredentials: CreateBarberWithCredentials = {
        ...formData,
        email,
        password,
      };
      await onSubmit(dataWithCredentials);
    } else {
      await onSubmit(formData);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    setUploading(true);
    try {
      if (barber?.barberId) {
        // For existing barbers, upload directly and update Firestore
        const url = await uploadPhoto(barber.barberId, file);
        setFormData(prev => ({ ...prev, photoURL: url }));
      } else {
        // For new barbers, upload to temporary storage without updating Firestore
        // We'll use Firebase Storage SDK directly to avoid the Firestore update
        const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
        const { storage } = await import('@/lib/firebase');

        const ext = file.name.split('.').pop();
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileRef = storageRef(storage, `barbers/${tempId}/photo_${Date.now()}.${ext}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        return new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            null,
            (error) => {
              console.error('Upload error:', error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                setFormData(prev => ({ ...prev, photoURL: downloadURL }));
                resolve();
              } catch (error) {
                reject(error);
              } finally {
                setUploading(false);
              }
            }
          );
        });
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploading(false);
    }
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !formData.specialties.includes(specialtyInput.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialtyInput.trim()],
      }));
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty),
    }));
  };

  const handleScheduleChange = (
    day: typeof DAYS[number],
    field: 'isWorking' | 'workStart' | 'workEnd' | 'breakStart' | 'breakEnd',
    value: boolean | string
  ) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value,
        },
      },
    }));
  };

  return (
    <Card className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">
        {barber ? 'Edit Barber' : 'Create New Barber'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">Photo</label>
          <ImageUpload
            currentImage={formData.photoURL || undefined}
            onUpload={handlePhotoUpload}
            uploading={uploading}
          />
        </div>

        {/* Display Name */}
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-ink-muted mb-2">
            Display Name *
          </label>
          <input
            id="displayName"
            type="text"
            required
            value={formData.displayName}
            onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            placeholder="John Doe"
          />
        </div>

        {/* Email y Password - Solo para crear nuevo barbero */}
        {!isEditing && (
          <div className="p-4 bg-info/10 rounded-lg border border-info/30">
            <h3 className="text-sm font-semibold text-info mb-3">
              Credenciales de Acceso
            </h3>
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
                  placeholder="barbero@ejemplo.com"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-ink-muted mb-2">
                  Contraseña *
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-muted mb-2">
                  Confirmar Contraseña *
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
                  placeholder="Repite la contraseña"
                />
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-sm text-danger mt-1">Las contraseñas no coinciden</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Email - Solo lectura cuando se edita */}
        {isEditing && barber?.email && (
          <div className="p-4 bg-surface-2 rounded-lg border border-hairline">
            <h3 className="text-sm font-semibold text-ink-muted mb-3">
              Información de Cuenta
            </h3>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Email
              </label>
              <input
                type="email"
                value={barber.email}
                disabled
                className="w-full px-4 py-2 border border-hairline bg-surface-2 text-ink-muted rounded-lg bg-surface-2 cursor-not-allowed"
              />
              <p className="text-xs text-ink-subtle mt-1">
                El email no se puede modificar. Para cambiar credenciales, contacta al administrador.
              </p>
            </div>
          </div>
        )}

        {/* Bio */}
        <div>
          <label htmlFor="bio" className="block text-sm font-medium text-ink-muted mb-2">
            Bio
          </label>
          <textarea
            id="bio"
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            placeholder="Brief bio about the barber"
          />
          <p className="text-xs text-ink-subtle mt-1">{(formData.bio || '').length}/500 characters</p>
        </div>

        {/* Specialties */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">Specialties</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={specialtyInput}
              onChange={(e) => setSpecialtyInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
              className="flex-1 px-4 py-2 border border-hairline bg-surface-3 text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
              placeholder="Add specialty (e.g., Fade, Beard Trim)"
            />
            <Button type="button" onClick={addSpecialty}>Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.specialties.map((specialty, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-info/10 text-info rounded-full text-sm flex items-center gap-2"
              >
                {specialty}
                <button
                  type="button"
                  onClick={() => removeSpecialty(specialty)}
                  className="hover:text-info"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-4">Work Schedule</label>
          <div className="space-y-3">
            {DAYS.map((day) => (
              <div key={day} className="p-3 bg-surface-2 rounded-lg">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-32">
                    <input
                      type="checkbox"
                      id={`${day}-working`}
                      checked={formData.schedule[day].isWorking}
                      onChange={(e) => handleScheduleChange(day, 'isWorking', e.target.checked)}
                      className="w-4 h-4 text-info border-hairline rounded focus:ring-focus mr-2"
                    />
                    <label htmlFor={`${day}-working`} className="text-sm font-medium capitalize text-ink-muted">
                      {day}
                    </label>
                  </div>

                  {formData.schedule[day].isWorking && (
                    <div className="flex gap-4 flex-wrap">
                      <div>
                        <label className="text-xs text-ink-muted">Start</label>
                        <input
                          type="time"
                          value={formData.schedule[day].workStart || ''}
                          onChange={(e) => handleScheduleChange(day, 'workStart', e.target.value)}
                          className="block px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-ink-muted">End</label>
                        <input
                          type="time"
                          value={formData.schedule[day].workEnd || ''}
                          onChange={(e) => handleScheduleChange(day, 'workEnd', e.target.value)}
                          className="block px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-ink-muted">Break Start</label>
                        <input
                          type="time"
                          value={formData.schedule[day].breakStart || ''}
                          onChange={(e) => handleScheduleChange(day, 'breakStart', e.target.value)}
                          className="block px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-ink-muted">Break End</label>
                        <input
                          type="time"
                          value={formData.schedule[day].breakEnd || ''}
                          onChange={(e) => handleScheduleChange(day, 'breakEnd', e.target.value)}
                          className="block px-2 py-1 border border-hairline bg-surface-3 text-ink rounded text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status Checkboxes */}
        <div className="flex gap-6">
          <div className="flex items-center">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="w-4 h-4 text-info border-hairline rounded focus:ring-focus"
            />
            <label htmlFor="isActive" className="ml-2 text-sm font-medium text-ink-muted">
              Active
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="isAvailable"
              type="checkbox"
              checked={formData.isAvailable}
              onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
              className="w-4 h-4 text-success border-hairline rounded focus:ring-success"
            />
            <label htmlFor="isAvailable" className="ml-2 text-sm font-medium text-ink-muted">
              Available for Bookings
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" isLoading={isLoading} className="flex-1">
            {barber ? 'Update Barber' : 'Create Barber'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading} className="flex-1">
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
